import type {
  AudienceData,
  BlocklistData,
  BlocklistInput,
  BotsData,
  BulkExecResult,
  BulkValidateResult,
  CfRulesView,
  ClaimUpdateInput,
  CloudflareBlockAction,
  CloudflareCapabilities,
  CloudflareConnectionStatus,
  CloudflareSyncPlan,
  ConsolidationResult,
  ConversionsData,
  CreateProjectInput,
  CspCheckResult,
  FraudData,
  GeoData,
  Goal,
  GoalInput,
  GoogleAdsAccountNode,
  GoogleAdsAudit,
  GoogleAdsCampaignRow,
  GoogleAdsCampaignsResult,
  GoogleAdsClaims,
  GoogleAdsDetails,
  GoogleAdsIpExclusions,
  GoogleAdsSearchTermsResult,
  GoogleAdsSiteAccount,
  GoogleAdsStatus,
  GoogleAdsWatchState,
  GtmContainerOption,
  GtmInstallPlan,
  GtmInstallStatus,
  GtmStatus,
  IpSummaryData,
  LogData,
  LoginInput,
  ManagedAccount,
  OnboardingState,
  OverviewData,
  PasswordResetConfirmInput,
  PasswordResetRequestInput,
  Period,
  Project,
  ProjectWithSecret,
  RangeFraudData,
  RealtimeData,
  RecidiveData,
  ReviewData,
  RuleAdoptionData,
  SignupInput,
  SourcesData,
  StackDetect,
  TopIpsData,
  TrackerMethods,
  UpdateProjectInput,
  UsageSummary,
  User,
  VerdictInput,
  VerdictRow,
  VerifyEmailCodeInput,
  VisitDetail,
  VisitFilter,
  VisitsPage,
  ZarazStatus,
} from '@silospot/shared';

const API_URL = import.meta.env.PUBLIC_API_URL ?? 'http://localhost:3001';

export class ApiError extends Error {
  readonly status: number;
  /** The parsed problem body, so callers can read extension members (e.g. restorable). */
  readonly data: unknown;
  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/** A deleted-but-restorable site, carried by a 409 from createProject. */
export interface RestorableSite {
  projectId: string;
  domain: string;
  name: string;
  deletedAt: string | null;
  visits: number;
}

/** Extract the restorable site from a createProject error, if the API offered one. */
export function restorableFromError(e: unknown): RestorableSite | null {
  const data = (e as ApiError | null)?.data as { restorable?: RestorableSite } | undefined;
  return data?.restorable ?? null;
}

/**
 * The server's real reason for a failure, when it's worth showing to the user:
 * a human sentence (RFC 7807 `detail`), not a short machine code (e.g. "noproxy")
 * nor the generic fallback. Callers do `serverError(e) ?? t('...generic')` to
 * surface the actual cause when available and a localized message otherwise.
 */
export function serverError(e: unknown): string | undefined {
  const m = (e as { message?: string } | null)?.message?.trim();
  return m && m !== 'Request failed' && /\s/.test(m) ? m : undefined;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const problem = data as { detail?: string; title?: string } | null;
    throw new ApiError(
      response.status,
      problem?.detail ?? problem?.title ?? 'Request failed',
      data,
    );
  }

  return data as T;
}

interface UserResponse {
  user: User;
}

export const api = {
  me: () => request<UserResponse>('/auth/me'),
  updateLocale: (locale: string) =>
    request<UserResponse>('/auth/me', { method: 'PATCH', body: JSON.stringify({ locale }) }),
  login: (body: LoginInput) =>
    request<UserResponse>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  signup: (body: SignupInput) =>
    request<UserResponse>('/auth/signup', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  requestPasswordReset: (body: PasswordResetRequestInput) =>
    request<{ ok: boolean }>('/auth/password-reset-request', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  confirmPasswordReset: (body: PasswordResetConfirmInput) =>
    request<{ ok: boolean }>('/auth/password-reset-confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  verifyEmailCode: (body: VerifyEmailCodeInput) =>
    request<UserResponse>('/auth/verify-email-code', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resendVerificationCode: () =>
    request<{ ok: boolean }>('/auth/verify-email-resend', { method: 'POST' }),

  listProjects: () => request<{ projects: Project[] }>('/v1/projects'),
  getProject: (id: string) => request<{ project: ProjectWithSecret }>(`/v1/projects/${id}`),
  createProject: (body: CreateProjectInput, opts: { fresh?: boolean } = {}) =>
    request<{ project: ProjectWithSecret }>(`/v1/projects${opts.fresh ? '?fresh=1' : ''}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  restoreProject: (id: string) =>
    request<{ project: Project }>(`/v1/projects/${id}/restore`, { method: 'POST' }),
  purgeProject: (id: string) =>
    request<{ ok: boolean }>(`/v1/projects/${id}/purge`, { method: 'POST' }),
  updateProject: (id: string, body: UpdateProjectInput) =>
    request<{ project: Project }>(`/v1/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteProject: (id: string) => request<void>(`/v1/projects/${id}`, { method: 'DELETE' }),
  verifyProject: (id: string) =>
    request<{
      verified: boolean;
      firstHitAt: string | null;
      lastHitAt: string | null;
      codeDetected: boolean;
      fetchError: boolean;
    }>(`/v1/projects/${id}/verify`, { method: 'POST' }),
  // Connection verification: a headless visit produces a marked, EXCLUDED beacon.
  verifyConnection: (id: string) =>
    request<{ verified: boolean; verifiedAt: string | null }>(
      `/v1/projects/${id}/verify-connection`,
    ),
  startVerifyConnection: (id: string) =>
    request<{ started: boolean }>(`/v1/projects/${id}/verify-connection`, { method: 'POST' }),

  // --- Analytics ---------------------------------------------------------
  getOverview: (id: string, range: RangeParams) =>
    request<OverviewData>(`/v1/projects/${id}/analytics/overview?${rangeQs(range)}`),
  getSources: (id: string, range: RangeParams) =>
    request<SourcesData>(`/v1/projects/${id}/analytics/sources?${rangeQs(range)}`),
  getBots: (id: string, range: RangeParams) =>
    request<BotsData>(`/v1/projects/${id}/analytics/bots?${rangeQs(range)}`),
  getRecidive: (id: string, range: RangeParams) =>
    request<RecidiveData>(`/v1/projects/${id}/analytics/recidive?${rangeQs(range)}`),
  getRealtime: (id: string) => request<RealtimeData>(`/v1/projects/${id}/analytics/realtime`),
  getAudience: (id: string, range: RangeParams) =>
    request<AudienceData>(`/v1/projects/${id}/analytics/audience?${rangeQs(range)}`),
  getGeo: (id: string, range: RangeParams) =>
    request<GeoData>(`/v1/projects/${id}/analytics/geo?${rangeQs(range)}`),
  getFraud: (id: string, range: RangeParams) =>
    request<FraudData>(`/v1/projects/${id}/analytics/fraud?${rangeQs(range)}`),
  getRangeThreats: (id: string, range: RangeParams) =>
    request<RangeFraudData>(`/v1/projects/${id}/analytics/range-threats?${rangeQs(range)}`),
  getRuleAdoption: (id: string) =>
    request<RuleAdoptionData>(`/v1/projects/${id}/analytics/rule-adoption`),
  consolidateRanges: (id: string, list: string) =>
    request<ConsolidationResult>(`/v1/projects/${id}/analytics/range-consolidate`, {
      method: 'POST',
      body: JSON.stringify({ list }),
    }),
  getIpSummary: (id: string, range: RangeParams) =>
    request<IpSummaryData>(`/v1/projects/${id}/analytics/ip-summary?${rangeQs(range)}`),
  getLog: (id: string) => request<LogData>(`/v1/projects/${id}/analytics/log`),
  getReview: (id: string, range: RangeParams) =>
    request<ReviewData>(`/v1/projects/${id}/analytics/review?${rangeQs(range)}`),
  postVerdict: (id: string, body: VerdictInput) =>
    request<VerdictRow>(`/v1/projects/${id}/analytics/verdicts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getConversions: (id: string, range: RangeParams, mode: 'valid' | 'raw') =>
    request<ConversionsData>(
      `/v1/projects/${id}/analytics/conversions?${rangeQs(range)}&mode=${mode}`,
    ),
  listGoals: (id: string) => request<{ goals: Goal[] }>(`/v1/projects/${id}/goals`),
  createGoal: (id: string, body: GoalInput) =>
    request<{ goal: Goal }>(`/v1/projects/${id}/goals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateGoal: (id: string, goalId: number, body: GoalInput) =>
    request<{ goal: Goal }>(`/v1/projects/${id}/goals/${goalId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteGoal: (id: string, goalId: number) =>
    request<{ ok: boolean }>(`/v1/projects/${id}/goals/${goalId}`, { method: 'DELETE' }),
  // Google Ads integration (Palier C, read-only).
  googleAdsStatus: () => request<GoogleAdsStatus>('/v1/integrations/google-ads/status'),
  googleAdsConnect: (returnTo?: string) =>
    request<{ url: string }>(
      `/v1/integrations/google-ads/connect${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
    ),
  // Workspace pool of linkable (non-manager, named) accounts; ?q filters server-side.
  googleAdsAccountsPool: (q?: string) =>
    request<{ accounts: ManagedAccount[] }>(
      `/v1/integrations/google-ads/accounts${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  // Accounts linked to a specific site (project).
  googleAdsSiteAccounts: (projectId: string) =>
    request<{ accounts: GoogleAdsSiteAccount[] }>(`/v1/projects/${projectId}/google-ads/accounts`),
  googleAdsLinkAccount: (projectId: string, customerId: string) =>
    request<{ ok: boolean }>(`/v1/projects/${projectId}/google-ads/accounts`, {
      method: 'POST',
      body: JSON.stringify({ customerId }),
    }),
  googleAdsUnlinkAccount: (projectId: string, customerId: string) =>
    request<{ ok: boolean }>(`/v1/projects/${projectId}/google-ads/accounts/${customerId}`, {
      method: 'DELETE',
    }),
  // C-fix3 — campaign selection & sync (per site).
  googleAdsAccountTree: (projectId: string) =>
    request<{ accounts: GoogleAdsAccountNode[]; reauthRequired?: boolean }>(
      `/v1/projects/${projectId}/google-ads/account-tree`,
    ),
  googleAdsSiteCampaigns: (projectId: string, customerId: string, period?: 30 | 90) =>
    request<GoogleAdsCampaignsResult>(
      `/v1/projects/${projectId}/google-ads/campaigns?${new URLSearchParams({
        customerId,
        ...(period ? { period: String(period) } : {}),
      }).toString()}`,
    ),
  googleAdsWatched: (projectId: string, customerId: string) =>
    request<GoogleAdsWatchState>(
      `/v1/projects/${projectId}/google-ads/watched?customerId=${customerId}`,
    ),
  googleAdsSetWatched: (
    projectId: string,
    input: { customerId: string; campaignIds: string[]; periodDays?: 30 | 90; autoSync?: boolean },
  ) =>
    request<GoogleAdsWatchState>(`/v1/projects/${projectId}/google-ads/watched`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  googleAdsRemoveWatched: (projectId: string, customerId: string, campaignId: string) =>
    request<{ ok: boolean }>(
      `/v1/projects/${projectId}/google-ads/watched/${campaignId}?customerId=${customerId}`,
      { method: 'DELETE' },
    ),
  googleAdsSyncNow: (projectId: string, customerId: string) =>
    request<{ synced: boolean; daysFetched: number; clicksUpserted: number; newCampaigns: number }>(
      `/v1/projects/${projectId}/google-ads/sync-now?customerId=${customerId}`,
      { method: 'POST' },
    ),
  googleAdsCampaigns: (customerId?: string, loginCustomerId?: string) =>
    request<{ customerId: string; campaigns: GoogleAdsCampaignRow[] }>(
      `/v1/integrations/google-ads/campaigns?${new URLSearchParams({
        ...(customerId ? { customerId } : {}),
        ...(loginCustomerId ? { loginCustomerId } : {}),
      }).toString()}`,
    ),
  googleAdsDetails: (customerId: string, period?: string, loginCustomerId?: string) =>
    request<GoogleAdsDetails>(
      `/v1/integrations/google-ads/details?${new URLSearchParams({
        customerId,
        ...(period ? { period } : {}),
        ...(loginCustomerId ? { loginCustomerId } : {}),
      }).toString()}`,
    ),
  googleAdsIpExclusions: (customerId: string, projectId: string) =>
    request<GoogleAdsIpExclusions>(
      `/v1/integrations/google-ads/ip-exclusions?${new URLSearchParams({
        customerId,
        projectId,
      }).toString()}`,
    ),
  googleAdsSearchTerms: (customerId: string, loginCustomerId?: string) =>
    request<GoogleAdsSearchTermsResult>(
      `/v1/integrations/google-ads/search-terms?${new URLSearchParams({
        customerId,
        ...(loginCustomerId ? { loginCustomerId } : {}),
      }).toString()}`,
    ),
  googleAdsAudit: (
    customerId: string,
    period?: string,
    loginCustomerId?: string,
    refresh?: boolean,
  ) =>
    request<GoogleAdsAudit>(
      `/v1/integrations/google-ads/audit?${new URLSearchParams({
        customerId,
        ...(period ? { period } : {}),
        ...(loginCustomerId ? { loginCustomerId } : {}),
        ...(refresh ? { refresh: '1' } : {}),
      }).toString()}`,
    ),
  googleAdsClaims: (customerId: string, loginCustomerId?: string) =>
    request<GoogleAdsClaims>(
      `/v1/integrations/google-ads/claims?${new URLSearchParams({
        customerId,
        ...(loginCustomerId ? { loginCustomerId } : {}),
      }).toString()}`,
    ),
  googleAdsUpdateClaim: (body: ClaimUpdateInput) =>
    request<{ ok: boolean }>('/v1/integrations/google-ads/claims', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  googleAdsDisconnect: () =>
    request<{ ok: boolean }>('/v1/integrations/google-ads', { method: 'DELETE' }),
  getBlocklist: (id: string) => request<BlocklistData>(`/v1/projects/${id}/blocklist`),
  addBlocklist: (id: string, body: BlocklistInput) =>
    request<{ ok: boolean; id: number; enforced: boolean }>(`/v1/projects/${id}/blocklist`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeBlocklist: (id: string, entryId: number) =>
    request<{ ok: boolean }>(`/v1/projects/${id}/blocklist/${entryId}`, { method: 'DELETE' }),
  getTopIps: (id: string, range: RangeParams) =>
    request<TopIpsData>(`/v1/projects/${id}/analytics/top-ips?${rangeQs(range)}`),
  getVisits: (id: string, params: VisitsParams) =>
    request<VisitsPage>(`/v1/projects/${id}/analytics/visits?${visitsQs(params)}`),
  getVisitDetail: (id: string, visitId: string) =>
    request<VisitDetail>(`/v1/projects/${id}/analytics/visits/${visitId}`),

  getUsage: () => request<UsageSummary>('/v1/usage'),

  // Onboarding (C-fix2) — derived state for the dashboard lock + setup banner.
  getOnboarding: (projectId?: string) =>
    request<OnboardingState>(
      `/v1/onboarding${projectId ? `?project=${encodeURIComponent(projectId)}` : ''}`,
    ),

  // --- Cloudflare (Palier D) ---------------------------------------------
  cfStatus: (id: string) => request<CloudflareConnectionStatus>(`/v1/projects/${id}/cloudflare`),
  // 1-click OAuth connect: returns the Cloudflare consent URL to navigate to.
  cfOAuthUrl: (id: string, returnTo?: string) =>
    request<{ url: string }>(
      `/v1/projects/${id}/cloudflare/oauth/connect${
        returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''
      }`,
    ),
  cfDetect: (id: string, token: string) =>
    request<CloudflareCapabilities>(`/v1/projects/${id}/cloudflare/detect`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  cfConnect: (
    id: string,
    body: { token: string; zoneId: string; mode?: string; manualAllowed?: boolean },
  ) =>
    request<CloudflareConnectionStatus>(`/v1/projects/${id}/cloudflare/connect`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cfProvision: (id: string, confirm: boolean, action?: CloudflareBlockAction) =>
    request<{
      dryRun: boolean;
      plan: CloudflareProvisionPlan;
      capabilities?: CloudflareCapabilities;
      result?: { mode: string; listId: string | null; ruleId: string | null };
      status?: CloudflareConnectionStatus;
    }>(`/v1/projects/${id}/cloudflare/provision`, {
      method: 'POST',
      body: JSON.stringify({ confirm, ...(action ? { action } : {}) }),
    }),
  cfSync: (id: string, confirm: boolean) =>
    request<{ dryRun: boolean; plan: CloudflareSyncPlan; result?: CloudflareSyncResult }>(
      `/v1/projects/${id}/cloudflare/sync`,
      { method: 'POST', body: JSON.stringify({ confirm }) },
    ),
  cfBlock: (id: string, ip: string, action?: CloudflareBlockAction) =>
    request<{ ok: boolean }>(`/v1/projects/${id}/cloudflare/block`, {
      method: 'POST',
      body: JSON.stringify({ ip, ...(action ? { action } : {}) }),
    }),
  cfUnblock: (id: string, ip: string) =>
    request<{ ok: boolean }>(`/v1/projects/${id}/cloudflare/unblock`, {
      method: 'POST',
      body: JSON.stringify({ ip }),
    }),
  // Bulk block: validate a pasted list (no writes) → then send targets by chunks.
  cfBulkValidate: (id: string, raw: string) =>
    request<BulkValidateResult>(`/v1/projects/${id}/cloudflare/bulk-validate`, {
      method: 'POST',
      body: JSON.stringify({ raw }),
    }),
  cfBulkBlock: (id: string, targets: string[]) =>
    request<BulkExecResult>(`/v1/projects/${id}/cloudflare/bulk-block`, {
      method: 'POST',
      body: JSON.stringify({ targets }),
    }),
  cfBulkUnblock: (id: string, targets: string[]) =>
    request<BulkExecResult>(`/v1/projects/${id}/cloudflare/bulk-unblock`, {
      method: 'POST',
      body: JSON.stringify({ targets }),
    }),
  cfSetAuto: (id: string, body: { enabled: boolean; dryRun?: boolean; threshold?: number }) =>
    request<CloudflareConnectionStatus>(`/v1/projects/${id}/cloudflare/auto`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cfAutoPreview: (id: string) =>
    request<{
      threshold: number;
      candidates: number;
      filtered: { infra: number; residential: number; goodBot: number };
      fresh: number;
      sample: Array<{ target: string; score: number; kind: string }>;
      fill: { count: number; cap: number; ratio: number };
    }>(`/v1/projects/${id}/cloudflare/auto/preview`),
  cfJournal: (id: string) =>
    request<{ actions: CfJournalEntry[] }>(`/v1/projects/${id}/cloudflare/journal`),
  // Reconciliation: the real active rules (Cloudflare ↔ mirror), not the action feed.
  cfRules: (id: string) => request<CfRulesView>(`/v1/projects/${id}/cloudflare/rules`),
  // Set the economic ad-click policy: allowed clicks (threshold) + the enable gate.
  cfEconomicConfig: (id: string, body: { threshold?: number; enabled?: boolean }) =>
    request<{ threshold: number; enabled: boolean }>(
      `/v1/projects/${id}/cloudflare/economic-config`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // Human decision on an orphan (cf_only) edge rule: attach to tracking, or remove.
  cfOrphan: (id: string, cfItemId: string, action: 'attach' | 'remove') =>
    request<{ ok: boolean; action: string; status: string }>(
      `/v1/projects/${id}/cloudflare/rules/orphan`,
      { method: 'POST', body: JSON.stringify({ cfItemId, action }) },
    ),
  // Reactivate a manually-unblocked entity (end its exception).
  cfReactivate: (id: string, ip: string) =>
    request<{ reactivated: number }>(`/v1/projects/${id}/cloudflare/exceptions/reactivate`, {
      method: 'POST',
      body: JSON.stringify({ ip }),
    }),
  // Revoke trust immediately for an entity (real_client rows kept, marked revoked).
  revokeTrust: (id: string, ip: string) =>
    request<{ revoked: number }>(`/v1/projects/${id}/analytics/verdicts/revoke`, {
      method: 'POST',
      body: JSON.stringify({ ip }),
    }),
  cfDisconnect: (id: string, mode: 'token-only' | 'with-resources') =>
    request<{ ok: boolean; cleanup: CfCleanup | null }>(`/v1/projects/${id}/cloudflare`, {
      method: 'DELETE',
      body: JSON.stringify({ mode }),
    }),

  // --- Tracker install (Palier E) ----------------------------------------
  trackerMethods: () => request<TrackerMethods>('/v1/tracker/methods'),
  detectStack: (id: string) => request<StackDetect>(`/v1/projects/${id}/detect-stack`),
  // Read-only: does the site's CSP block the tracker domain? (CSP help panel)
  cspCheck: (id: string) => request<CspCheckResult>(`/v1/projects/${id}/csp-check`),
  gtmStatus: () => request<GtmStatus>('/v1/integrations/gtm/status'),
  gtmConnect: (returnTo?: string) =>
    request<{ url: string }>(
      `/v1/integrations/gtm/connect${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
    ),
  gtmDisconnect: () => request<{ ok: boolean }>('/v1/integrations/gtm', { method: 'DELETE' }),
  gtmInstallStatus: (id: string) => request<GtmInstallStatus>(`/v1/projects/${id}/gtm`),
  gtmContainers: (id: string) =>
    request<{ containers: GtmContainerOption[] }>(`/v1/projects/${id}/gtm/containers`),
  gtmInstall: (id: string, body: { accountId: string; containerId: string; confirm?: boolean }) =>
    request<{
      dryRun: boolean;
      plan?: GtmInstallPlan;
      status?: GtmInstallStatus;
      updated?: boolean;
    }>(`/v1/projects/${id}/gtm/install`, { method: 'POST', body: JSON.stringify(body) }),
  gtmUninstall: (id: string) =>
    request<{ ok: boolean; removed: boolean }>(`/v1/projects/${id}/gtm/uninstall`, {
      method: 'POST',
    }),
  zarazStatus: (id: string) => request<ZarazStatus>(`/v1/projects/${id}/cloudflare/zaraz`),
  zarazActivate: (id: string) =>
    request<{ ok: boolean }>(`/v1/projects/${id}/cloudflare/zaraz`, { method: 'POST' }),
  zarazDeactivate: (id: string) =>
    request<{ ok: boolean }>(`/v1/projects/${id}/cloudflare/zaraz`, { method: 'DELETE' }),
};

export interface CfJournalEntry {
  ip: string;
  action: 'block' | 'challenge' | 'unblock' | 'test';
  origin: 'auto' | 'manual' | 'test';
  rule: string;
  status: 'active' | 'removed';
  detail: string | null;
  at: string;
}
export interface CfCleanup {
  ruleDeleted: boolean;
  listDeleted: boolean;
  fallbackRulesDeleted: number;
}
export interface CloudflareProvisionPlan {
  mode: 'normal' | 'fallback';
  action: CloudflareBlockAction;
  steps: Array<{ kind: string; method?: string; endpoint?: string; summary: string }>;
}
export interface CloudflareSyncResult {
  pushed: number;
  removed: number;
  evicted: number;
  budgetReached: boolean;
}

export interface RangeParams {
  period: Period;
  from?: string;
  to?: string;
}

export interface VisitsParams extends RangeParams {
  filter?: VisitFilter;
  source?: string;
  country?: string;
  /** Exact IP search — IPv4 or IPv6. */
  ip?: string;
  cursor?: string;
  limit?: number;
}

function rangeQs(range: RangeParams): string {
  const sp = new URLSearchParams({ period: range.period });
  if (range.from) sp.set('from', range.from);
  if (range.to) sp.set('to', range.to);
  return sp.toString();
}

function visitsQs(params: VisitsParams): string {
  const sp = new URLSearchParams({ period: params.period });
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.filter && params.filter !== 'all') sp.set('filter', params.filter);
  if (params.source) sp.set('source', params.source);
  if (params.country) sp.set('country', params.country);
  if (params.ip) sp.set('ip', params.ip);
  if (params.cursor) sp.set('cursor', params.cursor);
  if (params.limit) sp.set('limit', String(params.limit));
  return sp.toString();
}

export const TRACK_URL = import.meta.env.PUBLIC_TRACK_URL ?? 'http://localhost:3002';
